import {
    type ContactSessionTrigger,
    type CreateContactSessionResponse,
} from "@wags/profile-contracts/contact-session.contract.ts";
import { getContactHandoffPath } from "@wags/tanstack-start/react";
import type { JsonValue } from "@wags/utils/json.ts";
import { CONTACT_FUNNEL_EVENTS } from "../contact-funnel";
import { kitsReceivedText } from "./contact-stage";
import { markKitsHeardFromViewer } from "./kits-heard";
import {
    contactSessionErrorReason,
    contactSessionQuery,
    createContactSession,
    markContactHandoffOpened,
    type ContactVerificationStatus,
} from "./contact-session-client";

import { attemptEvidence } from "./handoff-evidence";
import type { ObservationMode } from "./handoff-observation.contract";

type Capture = (event: string, properties: Record<string, JsonValue>) => void;

export function capturingContactSessionQuery({
    authUserId,
    listingId,
    trigger,
    capture,
    fetchImpl,
    question,
}: {
    authUserId: string;
    listingId: string;
    trigger: ContactSessionTrigger;
    capture: Capture;
    fetchImpl: typeof fetch;
    question?: string;
}) {
    return contactSessionQuery(
        authUserId,
        listingId,
        async () => {
            try {
                return await createContactSession(
                    listingId,
                    trigger,
                    fetchImpl,
                    question,
                );
            } catch (failure) {
                capture(CONTACT_FUNNEL_EVENTS.sessionCreateFailed, {
                    operation: "session_create",
                    reason: contactSessionErrorReason(
                        failure instanceof Error ? failure : null,
                    ),
                });
                throw failure;
            }
        },
        question,
    );
}

export function captureMissingSession(
    capture: Capture,
    sessionPrefetched: boolean,
    attemptId: string,
    mode: ObservationMode,
) {
    capture(CONTACT_FUNNEL_EVENTS.openMessagesResult, {
        ...attemptEvidence(attemptId, mode),
        session_state: "unresolved",
        redirected: false,
        sms_available: false,
        handoff_path: "session_missing",
        session_prefetched: sessionPrefetched,
    });
}

export function sendToMessages({
    session,
    verificationStatus,
    sessionPrefetched,
    canUseSmsHandoff,
    capture,
    fetchImpl,
    attemptId,
    observationMode,
    onNavigationRequested,
}: {
    session: CreateContactSessionResponse;
    verificationStatus: ContactVerificationStatus | undefined;
    sessionPrefetched: boolean;
    canUseSmsHandoff: boolean;
    capture: Capture;
    fetchImpl: typeof fetch;
    attemptId: string;
    observationMode: ObservationMode;
    onNavigationRequested?: () => void;
}) {
    const handoffPath = getContactHandoffPath({
        canUseSmsHandoff,
        smsUrl: session.handoff.smsUrl,
    });
    const navigates = handoffPath === "sms_navigation";
    const reused = kitsReceivedText(verificationStatus, session.session.status);
    capture(CONTACT_FUNNEL_EVENTS.openMessagesResult, {
        ...attemptEvidence(attemptId, observationMode),
        contact_session_id: session.session.id,
        session_state: "resolved",
        redirected: navigates,
        sms_available: navigates,
        handoff_path: handoffPath,
        intent: session.intent,
        session_prefetched: sessionPrefetched,
        ...(reused && { reused: true }),
    });
    if (reused) {
        markKitsHeardFromViewer();
    } else {
        void markContactHandoffOpened(
            session.session.id,
            session.intent,
            fetchImpl,
        ).then((reason) => {
            if (!reason) return;
            capture(CONTACT_FUNNEL_EVENTS.sessionCreateFailed, {
                operation: "handoff_opened",
                intent: session.intent,
                reason,
                ...attemptEvidence(attemptId, observationMode),
                contact_session_id: session.session.id,
            });
        });
    }
    if (navigates) {
        onNavigationRequested?.();
        window.location.href = session.handoff.smsUrl;
    }
    return navigates;
}
